import { cn } from '@/lib/cn';
import { For, type JSX, Show, createSignal, createUniqueId } from 'solid-js';
import { Markdown } from './Markdown';
import { Tabs } from './Tabs';

export interface MarkdownEditorProps {
  label?: string;
  placeholder?: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
}

// [label, aria label, before, after, placeholder]
const ACTIONS: readonly [string, string, string, string, string][] = [
  ['B', 'Bold', '**', '**', 'bold text'],
  ['I', 'Italic', '_', '_', 'italic text'],
  ['H', 'Heading', '## ', '', 'Heading'],
  ['•', 'Bulleted list', '- ', '', 'List item'],
  ['1.', 'Numbered list', '1. ', '', 'List item'],
  ['</>', 'Code', '`', '`', 'code'],
  ['Link', 'Link', '[', '](https://)', 'link text'],
  ['Image', 'Image from URL', '![', '](https://)', 'alt text'],
];

const TABS = [
  { id: 'write', label: 'Write' },
  { id: 'preview', label: 'Preview' },
] as const;

/** Markdown textarea with a formatting toolbar and a rendered Preview tab. */
export const MarkdownEditor = (props: MarkdownEditorProps): JSX.Element => {
  const [tab, setTab] = createSignal<string>('write');
  const id = `md-${createUniqueId()}`;
  let area: HTMLTextAreaElement | undefined;

  // Wrap the selection (or a placeholder) in the syntax, then reselect the inner text.
  const apply = (before: string, after: string, placeholder: string) => {
    if (!area) return;
    const { selectionStart: start, selectionEnd: end, value } = area;
    const inner = value.slice(start, end) || placeholder;
    props.onChange(value.slice(0, start) + before + inner + after + value.slice(end));
    queueMicrotask(() => {
      area?.focus();
      area?.setSelectionRange(start + before.length, start + before.length + inner.length);
    });
  };

  return (
    <div class="flex flex-col gap-1 text-sm">
      <Show when={props.label}>
        <label
          for={id}
          class={cn(
            'font-medium text-neutral-700 dark:text-neutral-300',
            props.required && "after:ml-0.5 after:text-red-500 after:content-['*']",
          )}
        >
          {props.label}
        </label>
      </Show>
      <div class="rounded-lg border border-neutral-300 bg-white transition focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500 motion-reduce:transition-none dark:border-neutral-700 dark:bg-neutral-900">
        <div class="flex flex-wrap items-center justify-between gap-2 px-2">
          <Tabs tabs={TABS} active={tab()} onChange={setTab} class="border-b-0" />
          <Show when={tab() === 'write'}>
            <div class="flex flex-wrap gap-0.5" role="toolbar" aria-label="Formatting">
              <For each={ACTIONS}>
                {([text, aria, before, after, placeholder]) => (
                  <button
                    type="button"
                    aria-label={aria}
                    title={aria}
                    onClick={() => apply(before, after, placeholder)}
                    class="min-w-7 rounded-md px-1.5 py-1 text-xs font-semibold text-neutral-600 transition-colors hover:bg-neutral-100 motion-reduce:transition-none dark:text-neutral-300 dark:hover:bg-neutral-800"
                  >
                    {text}
                  </button>
                )}
              </For>
            </div>
          </Show>
        </div>
        <textarea
          ref={area}
          id={id}
          hidden={tab() !== 'write'}
          required={props.required}
          placeholder={props.placeholder}
          value={props.value}
          onInput={(e) => props.onChange(e.currentTarget.value)}
          class="block min-h-32 w-full resize-y rounded-b-lg border-t border-neutral-200 bg-transparent px-3 py-2 font-mono text-sm text-neutral-900 outline-none dark:border-neutral-800 dark:text-neutral-100"
        />
        <Show when={tab() === 'preview'}>
          <div class="min-h-32 border-t border-neutral-200 px-3 py-2 dark:border-neutral-800">
            <Show
              when={props.value.trim()}
              fallback={<p class="text-neutral-400">Nothing to preview</p>}
            >
              <Markdown source={props.value} />
            </Show>
          </div>
        </Show>
      </div>
      <span class="text-xs text-neutral-500">Markdown supported</span>
    </div>
  );
};
